import { Button, Card, MenuItem } from "@blueprintjs/core";
import { Select } from "@blueprintjs/select";
import { ACTION_PAYLOAD_TYPE_TO_DISPLAY } from "@package/ui-apollo-components";
import {
  type ActionPayload,
  ActionPayloadType,
} from "@package/ui-gql/index-data-access";
import {
  type SchedulerEditActionListProps,
  type SchedulerEditActionProps,
} from "./actions/scheduler-edit-action-props";
import { SchedulerEditActionChangeLocationProfile } from "./actions/SchedulerEditActionChangeLocationProfile";
import { SchedulerEditActionEnablePost } from "./actions/SchedulerEditActionEnablePost";

function SchedulerEditActionPayload(props: SchedulerEditActionProps) {
  const typename = props.action?.__typename;

  switch (typename) {
    case "ActionPayloadPostEnabled": {
      return <SchedulerEditActionEnablePost {...props} />;
    }
    case "ActionPayloadLocationProfileChanged": {
      return <SchedulerEditActionChangeLocationProfile {...props} />;
    }
    default: {
      return null;
    }
  }
}

function SchedulerEditAction(props: SchedulerEditActionProps) {
  return (
    <Card compact>
      <Select<ActionPayloadType>
        activeItem={props.action?.type}
        className="mt-2 mb-4"
        disabled={props.disabled}
        fill
        filterable={false}
        items={Object.values(ActionPayloadType)}
        itemRenderer={(type, { handleClick, handleFocus, modifiers }) => (
          <MenuItem
            active={modifiers.active}
            disabled={modifiers.disabled}
            key={type}
            onClick={handleClick}
            onFocus={handleFocus}
            roleStructure="listoption"
            text={ACTION_PAYLOAD_TYPE_TO_DISPLAY[type]}
          />
        )}
        popoverProps={{ minimal: true }}
        onItemSelect={(type) => props.onChange(getDefaultActionPayload(type))}
      >
        <Button
          alignText="left"
          disabled={props.disabled}
          fill
          minimal
          outlined
          rightIcon="caret-down"
          text={
            props.action?.type
              ? ACTION_PAYLOAD_TYPE_TO_DISPLAY[props.action?.type]
              : "Select an Action"
          }
        />
      </Select>
      <SchedulerEditActionPayload {...props} />
      <div className="flex flex-col w-full items-end justify-center">
        <Button
          className="mt-2"
          disabled={props.disabled}
          icon="remove"
          intent="danger"
          minimal
          onClick={props.onDelete}
          text="Delete Action"
        />
      </div>
    </Card>
  );
}

export function SchedulerEditActionList(props: SchedulerEditActionListProps) {
  return (
    <>
      {props.actions?.map((action, index) => (
        <div key={`${index}-${action?.type}`} className="mb-4 w-full">
          <SchedulerEditAction
            action={action}
            disabled={props.disabled}
            key={index}
            onChange={(newAction) => {
              const newActions = [...(props.actions ?? [])];
              newActions[index] = newAction;
              props.onChange(newActions);
            }}
            onDelete={() => {
              const newActions = [...(props.actions ?? [])];
              newActions.splice(index, 1);
              props.onChange(newActions);
            }}
          />
        </div>
      ))}
      <Button
        className="mt-2"
        disabled={props.disabled}
        icon="add"
        intent="primary"
        minimal
        onClick={() => {
          const newActions = [
            ...(props.actions ?? []),
            getDefaultActionPayload(
              ActionPayloadType.ActionLocationProfileChanged,
            ),
          ];
          props.onChange(newActions);
        }}
        text="Add Action"
      />
    </>
  );
}

function getDefaultActionPayload(type: ActionPayloadType): ActionPayload {
  switch (type) {
    case ActionPayloadType.ActionPostEnabled: {
      return {
        __typename: "ActionPayloadPostEnabled",
        type,
        post: "",
        enabled: true,
      };
    }
    case ActionPayloadType.ActionLocationProfileChanged: {
      return {
        __typename: "ActionPayloadLocationProfileChanged",
        type,
        location: "",
        profile: "",
      };
    }
    default: {
      throw new Error(`Unknown action type: ${type}`);
    }
  }
}
